/**
 * CategoryPage — Smart routing component that renders 3 views based on URL params:
 *
 * 1. /products                           → All Product Lines overview
 * 2. /products/:productLine              → Product line landing (TH or DTH)
 * 3. /products/:productLine/:categorySlug → Category landing (TH) or subcategory grid (DTH)
 */
import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { PRODUCT_LINES } from '@data/index';
import Breadcrumbs from '@components/layout/Breadcrumbs';
import RichContentPage from '@pages/RichContentPage';
import { loadContentOverlay, getContentForSlug } from '@data/contentOverlay';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Resolve a category from a product line by matching the URL slug. Searches
 *  top-level categories first, then falls back to searching subcategories —
 *  the PDP breadcrumb links to `/products/{productLine}/{subcategory}`, which
 *  hits this second path. Returns null when nothing matches. */
function findCategory(productLineData, categorySlug) {
  if (!productLineData?.categories || !categorySlug) return null;
  for (const c of productLineData.categories) {
    if (c.route?.endsWith('/' + categorySlug)) return c;
    const stripped = c.id.replace(/^(th-|dth-|rotary-|raiseboring-|services-)/, '');
    if (stripped === categorySlug) return c;
    if (c.id === categorySlug) return c;
  }
  // Subcategory fallback — matches PDP breadcrumb pattern (e.g.
  // `/products/rotary/rotary-drill-pipes`).
  for (const c of productLineData.categories) {
    for (const sub of c.subcategories || []) {
      if (sub.id === categorySlug) return sub;
      if (sub.route?.endsWith('/' + categorySlug)) return sub;
    }
  }
  return null;
}

/** True when the matched node is a subcategory (leaf) rather than a
 *  parent category. Subcategories are identified by their PLP route
 *  (`/products/category/*`) — categories use `/products/{productLine}/*`. */
function isSubcategoryNode(node) {
  return !!node?.route?.startsWith('/products/category/');
}

/** Build breadcrumb items array for the current depth */
function buildBreadcrumbs(productLineData, category) {
  const items = [{ label: 'Products', to: '/products' }];

  if (productLineData) {
    items.push({
      label: productLineData.shortTitle || productLineData.title,
      to: category ? productLineData.route : null,
      isLast: !category,
    });
  }

  if (category) {
    items.push({
      label: category.shortTitle || category.title,
      to: null,
      isLast: true,
    });
  }

  // Mark last item
  items.forEach((item, i) => {
    item.isLast = i === items.length - 1;
    if (item.isLast) item.to = null;
  });

  return items;
}

// ---------------------------------------------------------------------------
// View 1 — Product Lines Overview (/products)
// ---------------------------------------------------------------------------

function ProductLinesOverview() {
  const lines = Object.values(PRODUCT_LINES);

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs items={[{ label: 'Products', to: null, isLast: true }]} />

      <h1 className="font-sandvik-headline font-normal text-3xl text-carbon-black mt-2 mb-2">
        Rock Tools Products
      </h1>
      <p className="text-n5 font-sandvik-text mb-8 max-w-2xl">
        Browse our complete range of drilling tools by product line.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {lines.map((pl) => (
          <div
            key={pl.id}
            className="border border-n2 bg-white p-6 flex flex-col gap-3 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-3xl text-sandvik-gold">
                {pl.icon || 'inventory_2'}
              </span>
              <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black">
                {pl.shortTitle || pl.title}
              </h2>
              {pl.comingSoon && (
                <span className="ml-auto text-xs font-sandvik-text tracking-wider bg-n1 text-n5 px-2 py-0.5">
                  Coming soon
                </span>
              )}
            </div>

            <p className="text-n5 font-sandvik-text text-sm leading-relaxed">
              {pl.introContent?.description || pl.description || ''}
            </p>

            {pl.available ? (
              <Link
                to={pl.route}
                className="mt-auto inline-flex items-center gap-1 text-electric-blue font-sandvik-text font-normal text-sm hover:underline"
              >
                Browse
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            ) : (
              <span className="mt-auto text-n5 font-sandvik-text text-sm italic">
                Available soon
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View 2 — Product Line Landing (/products/:productLine)
// ---------------------------------------------------------------------------

function ProductLineLanding({ productLineData }) {
  const breadcrumbs = buildBreadcrumbs(productLineData, null);
  const isTH = productLineData.id === 'top-hammer';
  const byApp = productLineData.byApplication;
  const categories = productLineData.categories || [];

  // Load overlay so per-category tiles can show marcom hero images +
  // one-line descriptions. Overlay content-overrides.json takes precedence
  // over crawl-sourced content — this is where local marcom-approved
  // images override Sandvik.com CDN URLs.
  const [overlayReady, setOverlayReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadContentOverlay().then(() => { if (!cancelled) setOverlayReady(true); });
    return () => { cancelled = true; };
  }, []);
  const plContent = overlayReady ? getContentForSlug(productLineData.id, { level: 'productLines' }) : null;
  const plHero = plContent?.heroImage;
  const plSourceUrl = plContent?.sourceUrl;

  return (
    <div>
      {/* Hero — marcom overlay hero image (md+ only) behind dark gradient */}
      <div className="relative bg-carbon-black">
        {plHero && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.55)), url("${plHero}")` }}
          />
        )}
        <div className="sandvik-container py-12 md:py-16 relative z-10">
          <Breadcrumbs items={breadcrumbs} className="[&_*]:!text-white/60 mb-2" />
          <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl text-white mb-3">
            {productLineData.introContent?.title || productLineData.title}
          </h1>
          <p className="text-white/80 font-sandvik-text max-w-3xl leading-relaxed">
            {productLineData.introContent?.description || plContent?.description || ''}
          </p>

          {productLineData.introContent?.features?.length > 0 && (
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {productLineData.introContent.features.map((feat, i) => (
                <li key={i} className="flex items-start gap-2 text-white/80 font-sandvik-text text-sm">
                  <span className="material-symbols-outlined text-sandvik-gold text-base mt-0.5">check_circle</span>
                  {feat}
                </li>
              ))}
            </ul>
          )}
          {plSourceUrl && (
            <a
              href={plSourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 hover:bg-white/20 transition-colors text-sm font-sandvik-text"
            >
              Read more on Sandvik.com
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      <div className="sandvik-container py-10">
        {/* Browse by Application — only TH has this */}
        {isTH && byApp?.length > 0 && (
          <section className="mb-12">
            <h2 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-6">
              Browse by Application
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {byApp.map((app) => (
                <div
                  key={app.id}
                  className="border border-n2 bg-white p-6"
                >
                  <h3 className="font-sandvik-headline font-normal text-lg text-carbon-black mb-3">
                    {app.title}
                  </h3>
                  {app.subcategories?.length > 0 && (
                    <ul className="space-y-2">
                      {app.subcategories.map((sub) => (
                        <li key={sub.id}>
                          <Link
                            to={sub.route}
                            className="text-electric-blue font-sandvik-text text-sm hover:underline inline-flex items-center gap-1"
                          >
                            {sub.title}
                            <span className="material-symbols-outlined text-sm">chevron_right</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Browse by Product */}
        <section>
          <h2 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-6">
            Browse by Product
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => {
              const slug = cat.route?.split('/').pop() || cat.id;
              const subcatCount = cat.subcategories?.length || 0;
              // Overlay hydration: look up this category's slug in the
              // overlay. Resolver falls through subcategories → categories
              // → productLines so a category like `raiseboring-cutters` can
              // inherit its subcategory's hero image + one-liner.
              const catContent = overlayReady ? getContentForSlug(cat.id) : null;
              const catImage = catContent?.heroImage;
              const catDesc = catContent?.description;

              return (
                <Link
                  key={cat.id}
                  to={`/products/${productLineData.id}/${slug}`}
                  className="group border border-n2 bg-white flex flex-col hover:shadow-md hover:border-electric-blue/40 transition-all overflow-hidden"
                >
                  {catImage ? (
                    <div className="bg-carbon-black h-40 overflow-hidden">
                      <img
                        src={catImage}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ) : (
                    <div className="bg-n1 h-40 flex items-center justify-center">
                      <span className="material-symbols-outlined text-5xl text-sandvik-gold">
                        {cat.icon || 'inventory_2'}
                      </span>
                    </div>
                  )}
                  <div className="p-5 flex flex-col gap-2 flex-1">
                    <h3 className="font-sandvik-headline font-normal text-base text-carbon-black group-hover:text-electric-blue transition-colors">
                      {cat.shortTitle || cat.title}
                    </h3>
                    {catDesc && (
                      <p className="text-n7 font-sandvik-text text-sm leading-relaxed line-clamp-4">
                        {catDesc}
                      </p>
                    )}
                    {!catDesc && subcatCount > 0 && (
                      <p className="text-n5 font-sandvik-text text-sm">
                        {subcatCount} {subcatCount === 1 ? 'subcategory' : 'subcategories'}
                      </p>
                    )}
                    <span className="mt-auto pt-2 inline-flex items-center gap-1 text-electric-blue font-sandvik-text font-normal text-sm group-hover:underline">
                      View products
                      <span className="material-symbols-outlined text-base">arrow_forward</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View 3a — TH Category Landing (/products/top-hammer/:categorySlug)
// ---------------------------------------------------------------------------

function THCategoryLanding({ productLineData, category }) {
  const breadcrumbs = buildBreadcrumbs(productLineData, category);
  const subcategories = category.subcategories || [];

  // Same overlay hydration as DTHCategoryGrid so every category landing
  // gets the crawl-sourced hero image, features, and Sandvik.com CTA when
  // available. TH categories where the crawl has no match (e.g. Shank/
  // Couplings/Aux) degrade to the gradient banner with no visual regression.
  const [overlayReady, setOverlayReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadContentOverlay().then(() => { if (!cancelled) setOverlayReady(true); });
    return () => { cancelled = true; };
  }, []);

  const catContent = overlayReady ? getContentForSlug(category.id) : null;
  const heroImage = catContent?.heroImage;
  const description = catContent?.description || category.description;
  const features = catContent?.features || [];
  const sourceUrl = catContent?.sourceUrl;

  return (
    <div>
      {/* Hero banner — hero image renders md+ only; mobile gets the gradient
          alone for a lighter, faster page. */}
      <div className="relative bg-gradient-to-r from-carbon-black to-gray-800">
        {heroImage && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url("${heroImage}")` }}
          />
        )}
        <div className="sandvik-container py-12 md:py-16 relative z-10">
          <Breadcrumbs items={breadcrumbs} className="[&_*]:!text-white/60 mb-2" />
          <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl text-white mb-2">
            {category.title}
          </h1>
          {category.heroText && (
            <p className="text-sandvik-gold font-sandvik-text text-lg mb-4">
              {category.heroText}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 hover:bg-white/20 transition-colors text-sm font-sandvik-text"
            >
              Read more on Sandvik.com
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      <div className="sandvik-container py-8">
        {/* Subcategory pills — horizontal scrollable */}
        {subcategories.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-6">
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={sub.route}
                className="flex-shrink-0 px-4 py-2 border border-n2 bg-white text-carbon-black font-sandvik-text text-sm hover:border-electric-blue hover:text-electric-blue transition-colors rounded-none whitespace-nowrap"
              >
                {sub.title}
              </Link>
            ))}
          </div>
        )}

        {/* Description (overlay-first, then nav) */}
        {description && (
          <p className="text-n7 font-sandvik-text leading-relaxed max-w-3xl mb-8">
            {description}
          </p>
        )}

        {/* Features & Details from overlay */}
        {features.length > 0 && (
          <div className="mb-10">
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
              Features & Details
            </h2>
            <ul className="space-y-3 max-w-3xl">
              {features.map((f, i) => (
                <li key={i} className="border-l-4 border-sandvik-gold pl-4">
                  <span className="font-sandvik-text font-medium text-carbon-black">{f.title}</span>
                  {f.body && (
                    <>
                      <span className="text-n5"> — </span>
                      <span className="text-n7 font-sandvik-text text-sm">{f.body}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Subcategory tiles — hydrate per-tile hero + description from overlay */}
        {subcategories.length > 0 && (
          <section>
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-5">
              Browse by product
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {subcategories.map((sub) => {
                const subContent = overlayReady ? getContentForSlug(sub.id, { level: 'subcategories' }) : null;
                const subImage = subContent?.heroImage;
                const subDesc = subContent?.description;
                return (
                  <Link
                    key={sub.id}
                    to={sub.route}
                    className="group border border-n2 bg-white flex flex-col hover:shadow-md hover:border-electric-blue/40 transition-all overflow-hidden"
                  >
                    {subImage && (
                      <div className="bg-carbon-black h-40 overflow-hidden hidden sm:block">
                        <img
                          src={subImage}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="p-5 flex flex-col gap-2 flex-1">
                      <h3 className="font-sandvik-headline font-normal text-base text-carbon-black group-hover:text-electric-blue transition-colors">
                        {sub.title}
                      </h3>
                      {subDesc && (
                        <p className="text-n7 font-sandvik-text text-sm leading-relaxed line-clamp-4">
                          {subDesc}
                        </p>
                      )}
                      <span className="mt-auto pt-2 inline-flex items-center gap-1 text-electric-blue font-sandvik-text text-sm font-normal group-hover:underline">
                        View products
                        <span className="material-symbols-outlined text-base">arrow_forward</span>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View 3b — DTH Category Grid (/products/down-the-hole/:categorySlug)
// No hero banner, no description. Just breadcrumbs + title + subcategory grid.
// ---------------------------------------------------------------------------

function DTHCategoryGrid({ productLineData, category }) {
  const breadcrumbs = buildBreadcrumbs(productLineData, category);
  const subcategories = category.subcategories || [];

  // Load overlay + look up content for the category AND each subcategory.
  // The overlay lookup returns null when there's no crawl-sourced content,
  // so every render path degrades cleanly to the plain grid.
  const [overlayReady, setOverlayReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadContentOverlay().then(() => { if (!cancelled) setOverlayReady(true); });
    return () => { cancelled = true; };
  }, []);

  const catContent = overlayReady ? getContentForSlug(category.id) : null;
  const heroImage = catContent?.heroImage;
  const description = catContent?.description || category.description;
  const features = catContent?.features || [];
  const sourceUrl = catContent?.sourceUrl;

  return (
    <div>
      {/* Hero band — hero image renders md+ only; mobile gets the gradient
          alone for a lighter, faster page. */}
      <div className="relative bg-gradient-to-r from-carbon-black to-gray-800">
        {heroImage && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url("${heroImage}")` }}
          />
        )}
        <div className="sandvik-container py-12 md:py-14 relative z-10">
          <Breadcrumbs items={breadcrumbs} className={heroImage ? '[&_*]:!text-white/60 mb-2' : '[&_*]:!text-white/70 mb-2'} />
          <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl text-white mb-2">
            {category.title}
          </h1>
          {category.heroText && (
            <p className="text-sandvik-gold font-sandvik-text text-lg mb-4">
              {category.heroText}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 hover:bg-white/20 transition-colors text-sm font-sandvik-text"
            >
              Read more on Sandvik.com
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      <div className="sandvik-container py-10">
        {description && (
          <div className="max-w-3xl mb-8">
            <p className="text-n7 font-sandvik-text leading-relaxed">{description}</p>
          </div>
        )}

        {features.length > 0 && (
          <div className="mb-10">
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
              Features & Details
            </h2>
            <ul className="space-y-3 max-w-3xl">
              {features.map((f, i) => (
                <li key={i} className="border-l-4 border-sandvik-gold pl-4">
                  <span className="font-sandvik-text font-medium text-carbon-black">{f.title}</span>
                  {f.body && (
                    <>
                      <span className="text-n5"> — </span>
                      <span className="text-n7 font-sandvik-text text-sm">{f.body}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {subcategories.length > 0 ? (
          <>
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
              Browse by product
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {subcategories.map((sub) => {
                const subContent = overlayReady ? getContentForSlug(sub.id, { level: 'subcategories' }) : null;
                const subImage = subContent?.heroImage;
                const subDesc = subContent?.description;
                return (
                  <Link
                    key={sub.id}
                    to={sub.route}
                    className="group border border-n2 bg-white flex flex-col hover:shadow-md hover:border-electric-blue/40 transition-all overflow-hidden"
                  >
                    {subImage ? (
                      <div className="bg-carbon-black h-40 overflow-hidden">
                        <img
                          src={subImage}
                          alt=""
                          loading="lazy"
                          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="bg-n1 h-40 flex items-center justify-center text-n4">
                        <span className="material-symbols-outlined text-5xl">image</span>
                      </div>
                    )}
                    <div className="p-5 flex flex-col gap-2 flex-1">
                      <h3 className="font-sandvik-headline font-normal text-base text-carbon-black group-hover:text-electric-blue transition-colors">
                        {sub.title}
                      </h3>
                      {subDesc && (
                        <p className="text-n7 font-sandvik-text text-sm leading-relaxed line-clamp-4">
                          {subDesc}
                        </p>
                      )}
                      <span className="mt-auto pt-2 inline-flex items-center gap-1 text-electric-blue font-sandvik-text text-sm font-normal group-hover:underline">
                        View products
                        <span className="material-symbols-outlined text-base">arrow_forward</span>
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-n5 font-sandvik-text">No subcategories available.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// View 3c — Subcategory Landing Stub (/products/{productLine}/{subcategory})
// Stub-only rendering: hero + placeholder description + "View products" CTA
// to the actual PLP. Rich content (long descriptions, application photos,
// selection guides) will be populated later from PIM PDFs / brochure content
// — see MRT-PWA-Analysis/parked-subcategory-landing-content.md.
// ---------------------------------------------------------------------------

function SubcategoryLandingStub({ productLineData, subcategory }) {
  // Locate the parent category so breadcrumbs render properly.
  const parentCategory = productLineData.categories?.find((c) =>
    c.subcategories?.some((s) => s.id === subcategory.id)
  );
  const breadcrumbs = buildBreadcrumbs(productLineData, parentCategory || subcategory);
  if (parentCategory) {
    breadcrumbs.push({ label: subcategory.title, to: null, isLast: true });
    breadcrumbs.forEach((item, i) => {
      item.isLast = i === breadcrumbs.length - 1;
      if (item.isLast) item.to = null;
    });
  }

  // D4 — hydrate the stub with content-overlay content (hero image,
  // features, detailed info, downloads, sourceUrl for Sandvik.com CTA).
  const [content, setContent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    loadContentOverlay().then(() => {
      if (!cancelled) setContent(getContentForSlug(subcategory.id));
    });
    return () => { cancelled = true; };
  }, [subcategory.id]);

  const heroImage = content?.heroImage;
  const description = content?.description || subcategory.description ||
    `Browse Sandvik's ${(subcategory.shortTitle || subcategory.title).toLowerCase()} range. Detailed application notes and selection guidance for this subcategory will be published shortly.`;
  const features = content?.features || [];
  const detailedInfo = content?.detailedInfo;
  const downloads = content?.downloads || [];
  const sourceUrl = content?.sourceUrl;

  return (
    <div>
      <div className="relative bg-gradient-to-r from-carbon-black to-gray-800">
        {heroImage && (
          <div
            className="absolute inset-0 hidden md:block bg-cover bg-center"
            style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url("${heroImage}")` }}
          />
        )}
        <div className="sandvik-container py-12 md:py-16 relative z-10">
          <Breadcrumbs items={breadcrumbs} className="[&_*]:!text-white/60 mb-2" />
          <h1 className="font-sandvik-headline font-normal text-3xl md:text-4xl text-white mb-2">
            {subcategory.shortTitle || subcategory.title}
          </h1>
          {parentCategory?.shortTitle && (
            <p className="text-sandvik-gold font-sandvik-text text-lg mb-4">
              {parentCategory.shortTitle} {productLineData.shortTitle || productLineData.title}
            </p>
          )}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 hover:bg-white/20 transition-colors text-sm font-sandvik-text"
            >
              Read more on Sandvik.com
              <ExternalLink size={16} />
            </a>
          )}
        </div>
      </div>

      <div className="sandvik-container py-10">
        <div className="max-w-3xl mb-8">
          <p className="text-n7 font-sandvik-text leading-relaxed">
            {description}
          </p>
        </div>

        {features.length > 0 && (
          <div className="mb-10">
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
              Features & Details
            </h2>
            <ul className="space-y-3 max-w-3xl">
              {features.map((f, i) => (
                <li key={i} className="border-l-4 border-sandvik-gold pl-4">
                  <span className="font-sandvik-text font-medium text-carbon-black">{f.title}</span>
                  {f.body && (
                    <>
                      <span className="text-n5"> — </span>
                      <span className="text-n7 font-sandvik-text text-sm">{f.body}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {detailedInfo && (
          <details className="mb-10 border border-n2 bg-n1/30 max-w-3xl">
            <summary className="cursor-pointer px-4 py-3 font-sandvik-text font-medium text-carbon-black hover:bg-n1">
              Learn more
            </summary>
            <div className="px-4 pb-4 pt-1 text-n7 font-sandvik-text leading-relaxed text-sm">
              {detailedInfo}
            </div>
          </details>
        )}

        {downloads.length > 0 && (
          <div className="mb-10">
            <h2 className="font-sandvik-headline font-normal text-xl text-carbon-black mb-4">
              Downloads
            </h2>
            <ul className="space-y-2">
              {downloads.map((dl, i) => (
                <li key={i}>
                  <a
                    href={dl.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-electric-blue font-sandvik-text text-sm hover:underline"
                  >
                    <ExternalLink size={14} />
                    {dl.title}
                    {dl.sizeLabel && <span className="text-n5">({dl.sizeLabel})</span>}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Link
          to={subcategory.route}
          className="inline-flex items-center gap-2 bg-electric-blue text-white px-6 py-3 font-sandvik-text font-normal text-sm rounded-none hover:bg-electric-blue/90 transition-colors"
        >
          View products
          <span className="material-symbols-outlined text-base">arrow_forward</span>
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error / 404 states
// ---------------------------------------------------------------------------

function NotFoundMessage({ title, message }) {
  return (
    <div className="sandvik-container py-16 text-center">
      <span className="material-symbols-outlined text-6xl text-n5 mb-4 block">search_off</span>
      <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-2">
        {title}
      </h1>
      <p className="text-n5 font-sandvik-text mb-6">{message}</p>
      <Link
        to="/products"
        className="inline-flex items-center gap-2 bg-electric-blue text-white px-6 py-3 font-sandvik-text font-normal text-sm rounded-none hover:bg-electric-blue/90 transition-colors"
      >
        <span className="material-symbols-outlined text-base">arrow_back</span>
        Back to Products
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function CategoryPage() {
  const { productLine, categorySlug } = useParams();

  // View 1: No params → Product Lines Overview
  if (!productLine) {
    return <ProductLinesOverview />;
  }

  // Resolve product line data
  const productLineData = PRODUCT_LINES[productLine];
  if (!productLineData) {
    return (
      <NotFoundMessage
        title="Product line not found"
        message={`We couldn't find a product line matching "${productLine}". Please check the URL or browse all product lines.`}
      />
    );
  }

  // View 2: Product line only → Product Line Landing
  if (!categorySlug) {
    // If product line is not available (coming soon), show a message
    if (!productLineData.available) {
      return (
        <div className="sandvik-container py-6">
          <Breadcrumbs items={buildBreadcrumbs(productLineData, null)} />
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-6xl text-sandvik-gold mb-4 block">
              {productLineData.icon || 'hourglass_empty'}
            </span>
            <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-2">
              {productLineData.shortTitle || productLineData.title}
            </h1>
            <p className="text-n5 font-sandvik-text mb-2">
              This product line is coming soon.
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-1 text-electric-blue font-sandvik-text font-normal text-sm hover:underline mt-4"
            >
              <span className="material-symbols-outlined text-base">arrow_back</span>
              Back to Products
            </Link>
          </div>
        </div>
      );
    }
    return <ProductLineLanding productLineData={productLineData} />;
  }

  // View 3: Product line + category slug → Category Landing
  const category = findCategory(productLineData, categorySlug);
  if (!category) {
    return (
      <NotFoundMessage
        title="Category not found"
        message={`We couldn't find a category matching "${categorySlug}" in ${productLineData.shortTitle || productLineData.title}. Please check the URL or browse the product line.`}
      />
    );
  }

  // Rich content page (D7 — Services value pillars + Centrevo sub-apps).
  // Any category or subcategory with renderAs:'rich' bypasses the PLP/grid
  // taxonomy and renders a marketing-style page hydrated from the overlay.
  if (category.renderAs === 'rich') {
    return <RichContentPage productLineData={productLineData} node={category} />;
  }

  // Subcategory URL (from PDP breadcrumbs or direct nav) — render the stub.
  if (isSubcategoryNode(category)) {
    return <SubcategoryLandingStub productLineData={productLineData} subcategory={category} />;
  }

  // TH gets the full category landing; DTH / Rotary get the simple subcategory grid
  const isTH = productLineData.id === 'top-hammer';
  if (isTH) {
    return <THCategoryLanding productLineData={productLineData} category={category} />;
  }

  return <DTHCategoryGrid productLineData={productLineData} category={category} />;
}
